import { useMemo, useState } from "react";
import { toast } from "sonner";
import { ChevronsUpDown, Check, Plus, Search } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { WORKSPACE } from "@/constants/testIds";
import { useWorkspace } from "@/context/WorkspaceContext";
import api from "@/lib/api";

const ROLE_LABEL = { owner: "Owner", admin: "Admin", analyst: "Analyst", viewer: "Viewer" };
const ROLE_RANK = { owner: 0, admin: 1, analyst: 2, viewer: 3 };

export function WorkspaceSwitcher() {
  const { workspaces, active, setActive, refresh } = useWorkspace();
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("");

  // Owned/admin workspaces first, then alphabetical — keeps the user's real
  // workspaces at the top even when they belong to many.
  const sorted = useMemo(
    () => [...workspaces].sort(
      (a, b) => (ROLE_RANK[a.role] ?? 9) - (ROLE_RANK[b.role] ?? 9) || a.name.localeCompare(b.name)
    ),
    [workspaces]
  );
  const shown = useMemo(
    () => sorted.filter((w) => w.name.toLowerCase().includes(filter.trim().toLowerCase())),
    [sorted, filter]
  );
  const showFilter = workspaces.length > 8;

  const onNew = async () => {
    const name = window.prompt("New workspace name");
    if (!name?.trim()) return;
    try {
      const { data } = await api.post("/workspaces", { name: name.trim() });
      await refresh();
      setActive(data.id);
      toast.success(`Created “${data.name}”`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create workspace");
    }
  };

  if (!active) return null;

  return (
    <DropdownMenu open={open} onOpenChange={(o) => { setOpen(o); if (!o) setFilter(""); }}>
      <DropdownMenuTrigger
        data-testid={WORKSPACE.switcher}
        className="inline-flex max-w-[210px] items-center gap-2 rounded-full border border-border/60 bg-card/50 px-3 py-1.5 font-mono text-xs transition-colors hover:border-accent/50"
      >
        <span className="inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
        <span className="max-w-[120px] truncate font-medium">{active.name}</span>
        <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64 font-mono">
        <DropdownMenuLabel className="flex items-center justify-between text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
          Workspaces
          <span className="normal-case tracking-normal">{workspaces.length}</span>
        </DropdownMenuLabel>
        {showFilter && (
          <div className="px-1.5 pb-1.5">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 h-3 w-3 -translate-y-1/2 text-muted-foreground" />
              <input
                data-testid={WORKSPACE.switcherFilter}
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                onKeyDown={(e) => e.stopPropagation()}
                placeholder="Filter…"
                className="h-7 w-full rounded-md border border-border/60 bg-background/60 pl-7 pr-2 text-xs outline-none focus:border-accent"
              />
            </div>
          </div>
        )}
        <div className="max-h-[50vh] overflow-y-auto">
          {shown.length === 0 ? (
            <p className="px-2 py-3 text-center text-xs text-muted-foreground">No matches</p>
          ) : shown.map((w) => (
            <DropdownMenuItem
              key={w.id}
              data-testid={WORKSPACE.switcherItem}
              onClick={() => setActive(w.id)}
              className="flex items-center justify-between text-sm"
            >
              <span className="truncate">{w.name}</span>
              {w.id === active.id ? <Check className="h-4 w-4 shrink-0 text-accent" /> : (
                <span className="shrink-0 text-[10px] uppercase text-muted-foreground">{ROLE_LABEL[w.role]}</span>
              )}
            </DropdownMenuItem>
          ))}
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuItem data-testid={WORKSPACE.newWorkspace} onClick={onNew} className="text-sm text-accent">
          <Plus className="mr-2 h-4 w-4" />
          New workspace
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
